import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Reports API functions
export const reportService = {
  // Get all reports
  getReports: async (params = {}) => {
    try {
      const response = await api.get('/reports', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching reports:', error);
      throw error;
    }
  },

  // Get report by ID
  getReport: async (id) => {
    try {
      const response = await api.get(`/reports/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching report:', error);
      throw error;
    }
  },

  // Create new report
  createReport: async (reportData) => {
    try {
      const response = await api.post('/reports', reportData);
      return response.data;
    } catch (error) {
      console.error('Error creating report:', error);
      throw error;
    }
  },

  // Update report
  updateReport: async (id, reportData) => {
    try {
      const response = await api.put(`/reports/${id}`, reportData);
      return response.data;
    } catch (error) {
      console.error('Error updating report:', error);
      throw error;
    }
  },

  // Delete report
  deleteReport: async (id) => {
    try {
      const response = await api.delete(`/reports/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting report:', error);
      throw error;
    }
  },

  // Generate report
  generateReport: async (id, filters = {}) => {
    const response = await api.post(`/reports/${id}/generate`, filters);
    return response.data;
  },

  // Dashboard and Chart Data
  getDashboardStats: async () => {
    const response = await api.get('/reports/dashboard/stats');
    return response.data;
  },

  getEmployeeChartData: async () => {
    const response = await api.get('/reports/charts/employees');
    return response.data;
  },

  getLeaveChartData: async () => {
    const response = await api.get('/reports/charts/leave');
    return response.data;
  },

  getAttendanceChartData: async () => {
    const response = await api.get('/reports/charts/attendance');
    return response.data;
  },

  getPayrollChartData: async () => {
    const response = await api.get('/reports/charts/payroll');
    return response.data;
  },

  getPerformanceChartData: async () => {
    const response = await api.get('/reports/charts/performance');
    return response.data;
  },

  // Get employee report data
  getEmployeeReport: async (params = {}) => {
    try {
      const response = await api.post('/reports/1/generate', { type: 'employee', ...params });
      return response.data;
    } catch (error) {
      console.error('Error fetching employee report:', error);
      throw error;
    }
  },

  // Get leave report data
  getLeaveReport: async (params = {}) => {
    try {
      const response = await api.post('/reports/2/generate', { type: 'leave', ...params });
      return response.data;
    } catch (error) {
      console.error('Error fetching leave report:', error);
      throw error;
    }
  },

  // Get payroll report data
  getPayrollReport: async (params = {}) => {
    try {
      const response = await api.post('/reports/3/generate', { type: 'payroll', ...params });
      return response.data;
    } catch (error) {
      console.error('Error fetching payroll report:', error);
      throw error;
    }
  },

  // Get performance report data
  getPerformanceReport: async (params = {}) => {
    try {
      const response = await api.post('/reports/4/generate', { type: 'performance', ...params });
      return response.data;
    } catch (error) {
      console.error('Error fetching performance report:', error);
      throw error;
    }
  },

  // Get request report data
  getRequestReport: async (params = {}) => {
    try {
      const response = await api.post('/reports/5/generate', { type: 'request', ...params });
      return response.data;
    } catch (error) {
      console.error('Error fetching request report:', error);
      throw error;
    }
  },

  // Export report to different formats
  exportReport: async (id, format = 'pdf', params = {}) => {
    try {
      const response = await api.post(`/reports/${id}/export`, { format, ...params }, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting report:', error);
      throw error;
    }
  }
};

export default reportService; 